/**
 * Loan amortization and minimum-payment payoff helpers.
 *
 * All math is discrete (no continuous compounding).
 * i = annualRate / periodsPerYear
 * N = periodsPerYear * years
 */

export interface AmortRow {
  period: number;
  interest: number;
  principal: number;
  balance: number;
}

/**
 * Level (equal) periodic payment for a fully-amortizing loan.
 * Formula: P * i / (1 - (1+i)^-N), degenerating to P/N at i=0.
 */
export function levelPayment(
  principal: number,
  annualRate: number,
  periodsPerYear: number,
  years: number,
): number {
  const i = annualRate / periodsPerYear;
  const N = periodsPerYear * years;
  return i === 0 ? principal / N : (principal * i) / (1 - Math.pow(1 + i, -N));
}

/**
 * Full amortization schedule — one row per period.
 * The last balance is clamped to 0 to absorb floating-point drift.
 */
export function amortizationSchedule(
  principal: number,
  annualRate: number,
  periodsPerYear: number,
  years: number,
): AmortRow[] {
  const i = annualRate / periodsPerYear;
  const N = periodsPerYear * years;
  const payment = levelPayment(principal, annualRate, periodsPerYear, years);

  const rows: AmortRow[] = [];
  let balance = principal;

  for (let period = 1; period <= N; period++) {
    const interest = balance * i;
    const principalPaid = payment - interest;
    balance -= principalPaid;
    rows.push({
      period,
      interest,
      principal: principalPaid,
      balance: Math.max(balance, 0),
    });
  }

  return rows;
}

export interface MinimumPaymentResult {
  periods: number;
  totalInterest: number;
  schedule: AmortRow[];
  paysOff: boolean;
}

/**
 * Simulate credit-card-style minimum payments.
 *
 * Each period: payment = max(balance * minRate, minFloor).
 * If the payment never exceeds interest (balance * i), the debt never shrinks —
 * detected up-front and returned as { paysOff: false } with a capped schedule.
 *
 * Otherwise iterate until balance ≤ 0, capped at 1200 periods (~100 years).
 */
export function minimumPaymentPayoff(
  balance: number,
  annualRate: number,
  periodsPerYear: number,
  minRate: number,
  minFloor: number,
): MinimumPaymentResult {
  const i = annualRate / periodsPerYear;
  const MAX_PERIODS = 1200;

  // Early exit: check whether the first payment can reduce principal at all.
  const firstPayment = Math.max(balance * minRate, minFloor);
  const firstInterest = balance * i;
  if (firstPayment <= firstInterest) {
    // Payment never covers interest — build a short illustrative schedule (12 periods).
    const schedule: AmortRow[] = [];
    let bal = balance;
    let totalInterest = 0;
    for (let period = 1; period <= Math.min(12, MAX_PERIODS); period++) {
      const interest = bal * i;
      const payment = Math.max(bal * minRate, minFloor);
      // Balance grows (or stays flat) — record the shortfall as negative principal paid.
      const principalPaid = payment - interest;
      bal -= principalPaid; // bal increases when principalPaid < 0
      totalInterest += interest;
      schedule.push({ period, interest, principal: principalPaid, balance: bal });
    }
    return { periods: schedule.length, totalInterest, schedule, paysOff: false };
  }

  // Normal path: iterate until paid off or capped.
  const schedule: AmortRow[] = [];
  let bal = balance;
  let totalInterest = 0;

  for (let period = 1; period <= MAX_PERIODS; period++) {
    const interest = bal * i;
    const payment = Math.min(Math.max(bal * minRate, minFloor), bal + interest);
    const principalPaid = payment - interest;
    bal -= principalPaid;
    totalInterest += interest;
    schedule.push({ period, interest, principal: principalPaid, balance: Math.max(bal, 0) });

    if (bal <= 0) {
      return { periods: period, totalInterest, schedule, paysOff: true };
    }
  }

  // Hit the cap without paying off — treat as non-amortising for practical purposes.
  return { periods: MAX_PERIODS, totalInterest, schedule, paysOff: false };
}
